import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './MessageDialog.module.scss';

interface MessageDialogProps {
  isOpen: boolean;
  title?: string;
  message: string;
  onClose: () => void;
  /** Optional data-testid for the overlay (for modal detection) */
  dataTestId?: string;
}

const MessageDialog: React.FC<MessageDialogProps> = ({
  isOpen,
  title = 'Notice',
  message,
  onClose,
  dataTestId = 'message-dialog-overlay'
}) => {
  // Close on Escape key when dialog is open
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="message-dialog-title"
      data-testid={dataTestId}
    >
      <div
        className={styles.popupContainer}
        data-testid="message-dialog-popup-container"
      >
        <div
          className={styles.contentSection}
          data-testid="message-dialog-content"
        >
          <h2
            id="message-dialog-title"
            className={styles.title}
            data-testid="message-dialog-title"
          >
            {title}
          </h2>
          <p className={styles.message} data-testid="message-dialog-message">
            {message}
          </p>
        </div>

        <div className={styles.buttonRow} data-testid="message-dialog-buttons">
          <button
            onClick={onClose}
            className={styles.buttonPrimary}
            data-testid="message-dialog-ok-button"
          >
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MessageDialog;
